import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Loader from 'components/common/loader';
import { IframeModalHot } from 'components/common/IframeModalHot/IframeModalHot';
import Page404 from 'containers/page404';
import { useGetHotYoterPageQuery, useGetHotYoterStatePanelQuery } from 'redux/api/content.api';
import { HOT_YOTER_DEFAULT_SLUG } from './constants';
import {
    HotYoterIntroSection,
    HotYoterPageHeader,
} from './components/HotYoterIntroSection';
import { HotYoterStateSection } from './components/HotYoterStateSection';
import { HotYoterGuestSection } from './components/HotYoterGuestSection';
import { HotYoterBenefitsSection } from './components/HotYoterBenefitsSection';
import { canViewPurchasedHotYoterBenefits } from '../new_header/components/hot-yoter-eligibles-popup/utils/getEligiblesContent';
import { getHotYoterStateText } from './utils/getHotYoterStateText';
import '../category_page/filters/index.scss';
import '../category_page/results/index.scss';
import './index.scss';

const hasStateSectionContent = (content, purchasedBenefits = []) =>
    Boolean(content?.title || content?.subtitle || purchasedBenefits.length > 0);

export function HotYoterBenefitsPage() {
    const { slug = HOT_YOTER_DEFAULT_SLUG } = useParams();
    const [regulationsUrl, setRegulationsUrl] = useState(null);
    const userData = useSelector((store) => store.config.UserData);
    const translations = useSelector((store) => store.config.Translations);
    const deviceState = useSelector((store) => store.state.deviceState);
    const isMobile = deviceState === 'mobile';

    const { data, isLoading, isError } = useGetHotYoterPageQuery({ slug });
    const { data: statePanel } = useGetHotYoterStatePanelQuery(undefined, {
        skip: !userData,
    });

    const pageTitle = data?.title || 'הטבות הוטיותר';

    const purchasedBenefits = useMemo(
        () => data?.purchasedBenefits
            ?? statePanel?.purchasedBenefits
            ?? userData?.purchasedBenefits
            ?? userData?.purchased_benefits
            ?? [],
        [data?.purchasedBenefits, statePanel?.purchasedBenefits, userData],
    );

    const visiblePurchasedBenefits = useMemo(() => {
        if (!canViewPurchasedHotYoterBenefits(userData)) {
            return [];
        }

        return purchasedBenefits;
    }, [userData, purchasedBenefits]);

    const stateContent = useMemo(() => {
        if (!userData) {
            return null;
        }

        return getHotYoterStateText(
            data?.stateTexts,
            userData,
            statePanel?.hotYoterStateTexts,
            translations,
        );
    }, [userData, data?.stateTexts, statePanel?.hotYoterStateTexts, translations]);

    const shouldShowStateSection = isMobile
        && userData
        && hasStateSectionContent(stateContent, visiblePurchasedBenefits);

    useEffect(() => {
        document.title = pageTitle;
    }, [pageTitle]);

    if (isLoading) {
        return <Loader className="initial-loader" />;
    }

    if (isError || !data) {
        return <Page404 />;
    }

    const headerText = data.howToGet?.headerTexts?.[0] ?? null;
    const benefitsSection = data.benefitsSection ?? {};

    return (
        <main className="hot-yoter-benefits-page category-page" testId="hot-yoter-benefits-page">
            <div className={`hot-yoter-page-top${!isMobile ? ' hot-yoter-page-top_desktop' : ''}`}>
                <HotYoterPageHeader
                    pageTitle={pageTitle}
                    slug={slug}
                    showTitle={isMobile}
                />

                {userData ? (
                    shouldShowStateSection ? (
                        <HotYoterStateSection
                            pageTitle={pageTitle}
                            content={stateContent}
                            purchasedBenefits={visiblePurchasedBenefits}
                        />
                    ) : null
                ) : (
                    <HotYoterGuestSection
                        message={data.guestSection?.text}
                        buttonLabel={data.guestSection?.buttonText}
                    />
                )}
            </div>

            <div className="hot-yoter-content-container content-container">
                <HotYoterIntroSection
                    pageTitle={pageTitle}
                    headerText={headerText}
                    howToGet={data.howToGet}
                    regulationsUrl={data.regulationsUrl}
                    onRegulationsPress={setRegulationsUrl}
                />

                <HotYoterBenefitsSection
                    benefitsGroups={benefitsSection.benefitsGroups ?? []}
                    searchPlaceholderText={benefitsSection.searchPlaceholderText}
                    relatedExpert={benefitsSection.relatedExpert}
                    pageTitle={pageTitle}
                />
            </div>

            <IframeModalHot
                testID="hot-yoter-regulations-modal"
                open={Boolean(regulationsUrl)}
                onClose={() => setRegulationsUrl(null)}
                src={regulationsUrl}
                title="תקנון הטבות הוטיותר"
                height={600}
            />
        </main>
    );
}

export default HotYoterBenefitsPage;
